export const getBookingKey = ({
  accountId,
  taxCodeId,
  costCenterId,
  invoiceId,
  createdAt,
  createdBy,
  unlinked,
}: {
  accountId: string | null;
  taxCodeId: string | null;
  costCenterId: string | null;
  invoiceId: string;
  createdAt: string;
  createdBy: string;
  unlinked: string | null;
}) =>
  `${accountId}-${taxCodeId}-${costCenterId}-${invoiceId}-${createdBy}-${createdAt?.replace(
    /[^0-9]/g,
    "",
  )}-${unlinked}`;

interface FastlaneModelType {
  lineItemId: string;
  tenantId: string;
  data: any;
}

export const groupByBooking = (lineItems: FastlaneModelType[]) => {
  const bookingsMap = new Map();

  for (const item of lineItems) {
    const lineItem = item.data.lineItemDetails.lineItem;
    const invoice = item.data.invoiceDetails.invoice;
    const supplier = item.data.invoiceDetails?.supplier ?? undefined;

    const bookingKey = getBookingKey({
      accountId: lineItem.accountId,
      taxCodeId: lineItem.taxCodeId,
      costCenterId: lineItem.costCenterId,
      invoiceId: invoice.invoiceId,
      createdAt: lineItem.createdAt,
      createdBy: lineItem.createdBy,
      unlinked: lineItem.unlinked,
    });

    if (!bookingsMap.has(bookingKey)) {
      bookingsMap.set(bookingKey, {
        id: bookingKey,
        invoiceId: invoice.invoiceId,
        accountId: lineItem.accountId,
        taxCodeId: lineItem.taxCodeId,
        costCenterId: lineItem.costCenterId,
        taxCodeConfidence: lineItem.taxCodeConfidence,
        accountJustification: lineItem.accountJustification,
        accountConfidence: lineItem.accountConfidence,
        amount: 0,
        nettoAmount: 0,
        amountConfidence: lineItem.amountConfidence,
        invoice,
        description: lineItem.bookingText,
        subRows: [],
        lineItemsCount: invoice.totalLineItems,
        buJustification: lineItem.buJustification,
        bookingText: lineItem.bookingText,
        vatRate: lineItem.vatRate,
        vatRateConfidence: lineItem.vatRateConfidence,
        supplier: supplier?.parentSupplier ?? supplier,
        tags: item.data.invoiceDetails.tags,
        unlinked: lineItem.unlinked,
      });
    }

    const booking = bookingsMap.get(bookingKey);

    booking.subRows.push({
      id: lineItem.lineItemId,
      invoiceId: invoice.invoiceId,
      accountId: lineItem.accountId,
      taxCodeId: lineItem.taxCodeId,
      costCenterId: lineItem.costCenterId,
      taxCodeConfidence: lineItem.taxCodeConfidence,
      accountJustification: lineItem.accountJustification,
      accountConfidence: lineItem.accountConfidence,
      amount: lineItem.amount,
      accountReviewed: lineItem.accountReviewed,
      description: lineItem.description ?? lineItem.name,
      buJustification: lineItem.buJustification,
      originalName: lineItem.name,
      bookingText: lineItem.bookingText,
      nettoAmount: lineItem.nettoAmount,
      amountConfidence: lineItem.amountConfidence,
      vatRate: lineItem.vatRate,
      vatRateConfidence: lineItem.vatRateConfidence,
      supplier: supplier?.parentSupplier ?? supplier,
      invoice,
      unlinked: lineItem.unlinked,
    });

    booking.amount = booking.amount + lineItem.amount;
    booking.nettoAmount = booking.nettoAmount + lineItem.nettoAmount;

    booking.accountReviewed =
      booking.accountReviewed && lineItem.accountReviewed;

    if (
      lineItem.accountConfidence === null ||
      lineItem.accountConfidence === undefined
    ) {
      booking.accountConfidence = null;
    } else if (
      booking.accountConfidence !== null &&
      lineItem.accountConfidence === 0
    ) {
      booking.accountConfidence = 0;
    } else if (
      booking.accountConfidence === 1 &&
      lineItem.accountConfidence === 1
    ) {
      booking.accountConfidence = 1;
    }

    if (
      lineItem.taxCodeConfidence === null ||
      lineItem.taxCodeConfidence === undefined
    ) {
      booking.taxCodeConfidence = null;
    } else if (
      booking.taxCodeConfidence !== null &&
      lineItem.taxCodeConfidence === 0
    ) {
      booking.taxCodeConfidence = 0;
    } else if (
      booking.taxCodeConfidence === 1 &&
      lineItem.taxCodeConfidence === 1
    ) {
      booking.taxCodeConfidence = 1;
    }

    if (
      lineItem.amountConfidence === null ||
      lineItem.amountConfidence === undefined
    ) {
      booking.amountConfidence = null;
    } else if (
      booking.amountConfidence !== null &&
      lineItem.amountConfidence === 0
    ) {
      booking.amountConfidence = 0;
    } else if (
      booking.amountConfidence !== null &&
      lineItem.amountConfidence === 1
    ) {
      booking.amountConfidence = 1;
    }
  }

  const bookings = Array.from(bookingsMap.values());

  return bookings;
};

export interface BookingSubRowType {
  id: string;
  invoiceId: string;
  accountId: string | null;
  taxCodeId: string | null;
  costCenterId: string | null;
  taxCodeConfidence: number | null;
  accountJustification: string | undefined;
  accountConfidence: number | null;
  amount: number;
  accountReviewed: boolean;
  description: string;
  buJustification: string | undefined;
  originalName: string;
  bookingText: string;
  nettoAmount: number;
  amountConfidence: number | null;
  vatRate: number | string;
  vatRateConfidence: number | null;
  supplier: any | undefined;
  invoice: any;
  unlinked: string | null;
}

export interface BookingType {
  id: string;
  invoiceId: string;
  accountId: string | null;
  taxCodeId: string | null;
  costCenterId: string | null;
  taxCodeConfidence: number | null;
  accountJustification: string | undefined;
  accountConfidence: number | null;
  amount: number;
  nettoAmount: number;
  amountConfidence: number | null;
  invoice: any;
  description: string;
  subRows: BookingSubRowType[];
  lineItemsCount: number;
  buJustification: string | undefined;
  bookingText: string;
  vatRate: number | string;
  vatRateConfidence: number | null;
  supplier: any | undefined;
  tags: unknown[];
  accountReviewed?: boolean;
}
